import apiInstance from '../config/HttpCommon';

// New API interfaces for district customer site info
export interface SiteInfo {
  siteId: number;
  name: string;
  cnxSiteId: number;
}

export interface DistrictCustomerSiteInfo {
  salesDistrictCustomer: string;
  sites: SiteInfo[];
}

// Interface for Customer Account Lite response
export interface CustomerAccountLite {
  id: string;
  customerId: string;
  customerName: string;
  salesDistrict: string;
  salesDistrictCustomer: string;
}

// New API function to get district customer site info
export const getDistrictCustomerSiteInfo = async (): Promise<DistrictCustomerSiteInfo[]> => {
  try {
    const response = await apiInstance.get('/OnBoarding/GetDistrictCustomerSiteInfo');    
    
    // Handle different response formats
    let data = response.data;
    
    // If the response is wrapped in a success object, extract the data
    if (data && typeof data === 'object' && data.Data) {
      data = data.Data;
    }
    
    // Ensure we have an array
    if (!Array.isArray(data)) {
      console.warn('Expected array from API, received:', data);
      return [];
    }
    
    return data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching district customer site info:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

// New API function to get customer accounts lite
export const getAccountsLite = async (): Promise<CustomerAccountLite[]> => {
  try {
    const response = await apiInstance.get('/Customer/GetAccountsLite');
    
    // Log the response to debug the structure
    
    // Handle different response formats
    let data = response.data;
    
    // If the response is wrapped in a success object, extract the data
    if (data && typeof data === 'object' && data.Data) {
      data = data.Data;
    }
    
    // Ensure we have an array
    if (!Array.isArray(data)) {
      console.warn('Expected array from GetAccountsLite API, received:', data);
      return [];
    }
    
    return data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching customer accounts lite:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

export const deleteUserById = async (userId: string) => {
  try {
    const response = await apiInstance.get(`/User/Delete/${userId}`);
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error deleting user:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
}

// Upload master variables CSV file
export const uploadMasterVariables = async (file: File): Promise<string> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiInstance.post('/AssetType/UploadMasterVariables', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error uploading master variables file:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

// Download master variables CSV file
export const downloadMasterVariables = async () => {
  try {
    const response = await apiInstance.get('/AssetType/DownloadMasterVariables', {
      responseType: 'blob',
      headers: {
        'Content-Type': 'text/csv',
      },
    });

    return response;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error downloading master variables file:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

export const getUsers = async (userType: string, customerId?: string) => {
  try {
    let url = `/User/GetUsers/${userType}`;

    // Add customerId as query parameter if provided
    if (customerId) {
      url += `/${customerId}`;
    }

    const response = await apiInstance.get(url);
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching Users Data:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};
export const getUserType = async (userType: string, customerId?: string) => {
  try {
    let url = `/User/GetUsers/${userType}`;
    if (customerId) {
      url += `/${customerId}`;
    }
    const response = await apiInstance.get(url);
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching user type:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
}

export interface SaveUserPayload {
  id?: string;
  customerId?: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  mobilePhoneCode: string;
  mobilePhoneNo: string;
  officePhoneCode: string;
  officePhoneNo: string;
  type: string;
  role: string;
  status: string;
  isSsoEnabled: boolean;
  accessVisibility: string[];
  salesDistrict: string;
  salesRegion: string;
}

export const saveUser = async (userData: SaveUserPayload, userType: string) => {
  try {
    // Remove customerId if user type is Internal
    const payload = userType === 'Internal'
      ? { ...userData, customerId: undefined }
      : userData;

    const response = await apiInstance.post('/User/SaveUser', payload, {
      validateStatus: function (status) {
        // Accept both success and client error status codes
        return status >= 200 && status < 500;
      }
    });

    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;

    console.error('Error saving user:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });

    // Return a generic error response if we can't get the API response
    return {
      Success: false,
      Message: 'Failed to save user. Please try again.',
      Data: {},
      StatusCode: 500
    };
  }
}

export const getUserById = async (userId: string) => {
  try {
    const response = await apiInstance.get(`/User/Get/${userId}`);
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching user by ID:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

export const uploadConfigData = async (id: string) => {
  try {
    const response = await apiInstance.get(`/Asset/UploadConfigData/${id}`);
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error uploading config data:', {
      message: axiosError.message,
      response: axiosError.response?.data,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    // Return the error response instead of throwing
    return axiosError.response?.data || {
      Success: false,
      Message: 'An unexpected error occurred',
      Data: {},
      StatusCode: 500
    };
  }
}


export const getCustomersAndSites = async () => {
  try {
    const response = await apiInstance.get('/Plant/GetCustomersAndSites');
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching customers and sites:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
}

export const userDownload = async (userType: string, customerId?: string) => {
  try {
    let url = `/User/Download/${userType}`;

    // Add customerId as query parameter if provided
    if (customerId) {
      url += `?customerId=${customerId}`;
    }

    const response = await apiInstance.get(url, {
      responseType: 'blob',  // Add this line to handle CSV downloads
      headers: {
        'Content-Type': 'text/csv'
      }
    });

    return response;  // Return the full response, not just response.data
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching Users Data:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

export const getPlantSites = async () => {
  try {
    const response = await apiInstance.get('/Plant/GetSites');
    return response.data; // Assuming the API returns an array of sites
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching plant sites:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

export interface SitePayload {
  id: string; // UUID for the site
  customerName: string; // Customer name - required by API
  soldTo: string; // Sold To number
  siteName: string;
  siteStatus: string;
  country: string;
  salesDistrict: string;
  salesRegion: string;
  state: string;
  city: string;
  latitude: string;
  longitude: string;
}

// Define the expected structure of a site's details when fetched
// Adjust property names to match what your GET /Site/GetSiteByName/{siteName} returns
export interface SiteDetails {
  siteName: string;
  customer: string; // Or customerName if it's not an ID
  country: string;
  salesDistrict: string;
  state: string;
  salesRegion: string;
  city: string;
  status: string;
  latitude: string;
  longitude: string;
  // Add other properties that the GET endpoint for a single site returns
  siteId?: string; // If your backend returns an ID, include it here
}

export const createSite = async (siteData: SitePayload) => {
  try {
    const response = await apiInstance.post('/Site/SaveSite', siteData);
    return response;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error creating site:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

// Helper function specifically for creating sites from customer data
export const createSiteFromCustomerData = async (customerData: {
  customerName: string;
  soldTo: string;
  country: string;
  salesDistrict: string;
  salesRegion: string;
  state: string;
  city: string;
}, siteData: {
  siteName: string;
  siteStatus?: string;
  latitude?: string;
  longitude?: string;
}) => {
  const { v4: uuidv4 } = await import('uuid');

  const payload: SitePayload = {
    id: uuidv4(),
    customerName: customerData.customerName,
    soldTo: customerData.soldTo,
    siteName: siteData.siteName,
    siteStatus: siteData.siteStatus || 'Active',
    country: customerData.country,
    salesDistrict: customerData.salesDistrict,
    salesRegion: customerData.salesRegion,
    state: customerData.state,
    city: customerData.city,
    latitude: siteData.latitude || '0',
    longitude: siteData.longitude || '0'
  };

  return createSite(payload);
};

export const getSitesList = () => {
  return apiInstance.get('/Site/GetSites');
};
export const getSitesLite = async (customerId: string) => {
  try {
    const response = await apiInstance.get(`/Site/GetSitesLite/${customerId}`);
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching sites list lite:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

export const getPlantsList = () => {
  return apiInstance.get('/Plant/GetPlants');
};

export const getPlantById = (plantId: string) => {
  return apiInstance.get(`/Plant/GetPlant/${plantId}`);
};
export const getPlantsBySite = (siteId: string) => {
  return apiInstance.get(`/Plant/GetPlantsBySite/${siteId}`);
};

export interface PaginatedAssetSummaryResponse {
  items: AssetSummaryItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export const parsePaginatedSummaryResponse = (responseData: unknown): PaginatedAssetSummaryResponse => {
  const wrapped = responseData as { Data?: unknown };
  const data = wrapped && 'Data' in wrapped ? wrapped.Data : responseData;

  if (data == null) {
    return { items: [], totalCount: 0, pageNumber: 1, pageSize: 0, totalPages: 0 };
  }

  if (data && typeof data === 'object' && !Array.isArray(data) && 'items' in data) {
    const paginated = data as PaginatedAssetSummaryResponse;
    return {
      items: Array.isArray(paginated.items) ? paginated.items : [],
      totalCount: paginated.totalCount ?? 0,
      pageNumber: paginated.pageNumber ?? 1,
      pageSize: paginated.pageSize ?? 0,
      totalPages: paginated.totalPages ?? 0,
    };
  }

  if (Array.isArray(data)) {
    return {
      items: data as AssetSummaryItem[],
      totalCount: data.length,
      pageNumber: 1,
      pageSize: data.length,
      totalPages: 1,
    };
  }

  return { items: [], totalCount: 0, pageNumber: 1, pageSize: 0, totalPages: 0 };
};

const buildSummaryUrl = (
  endpoint: 'GetSummary' | 'GetPinnedSummary',
  pageNumber: number,
  pageSize: number,
  customerId?: string,
  siteId?: string,
  plantId?: string
): string => {
  let url = `/Asset/${endpoint}/${pageNumber}/${pageSize}`;

  if (customerId) {
    url += `/${customerId}`;
    if (siteId) {
      url += `/${siteId}`;
      if (plantId) {
        url += `/${plantId}`;
      }
    }
  }

  return url;
};

export const getAssetSummary = (
  pageNumber: number,
  pageSize: number,
  customerId?: string,
  siteId?: string,
  plantId?: string
) => {
  return apiInstance.get(buildSummaryUrl('GetSummary', pageNumber, pageSize, customerId, siteId, plantId));
};

export const getPinnedAssetSummary = (
  pageNumber: number,
  pageSize: number,
  customerId?: string,
  siteId?: string,
  plantId?: string
) => {
  return apiInstance.get(buildSummaryUrl('GetPinnedSummary', pageNumber, pageSize, customerId, siteId, plantId));
};

export const getGatewayStatus = async (gatewayDeviceId: string): Promise<string> => {
  if (!gatewayDeviceId) return '';
  try {
    const response = await apiInstance.get(`/Asset/GetGatewayStatus/${gatewayDeviceId}`);
    const data = response.data?.Data ?? response.data;
    return typeof data === 'string' ? data : String(data ?? '');
  } catch {
    return '';
  }
};

export const fetchAllAssetSummaryPages = async (
  customerId?: string,
  siteId?: string,
  plantId?: string,
  pageSize = 500
): Promise<AssetSummaryItem[]> => {
  const allItems: AssetSummaryItem[] = [];
  let pageNumber = 1;
  let totalPages = 1;

  do {
    const response = await getAssetSummary(pageNumber, pageSize, customerId, siteId, plantId);
    const parsed = parsePaginatedSummaryResponse(response.data);
    allItems.push(...parsed.items);
    totalPages = parsed.totalPages || 1;
    pageNumber += 1;
  } while (pageNumber <= totalPages);

  return allItems;
};


export const deletePlantById = (plantId: string) => {
  return apiInstance.get(`/Plant/Delete/${plantId}`);
};

export const deletePlant = (plantId: string) => {
  return apiInstance.get(`/Plant/Delete/${plantId}`);
};

// Get site details by ID
export const getSiteById = (siteId: string) => {
  return apiInstance.get(`/Site/GetSite/${siteId}`);
};
//get site by customer id
export const getSiteByCustomer = (customerId: string, soldTo: string) => {
  return apiInstance.get(`/Site/GetSitesByCustomer?customerName=${customerId}&soldTo=${soldTo}`);
};

// --- NEW FUNCTION TO FETCH SINGLE SITE DETAILS ---
// Assuming your backend has an endpoint like /Site/GetSiteByName
export const getSiteDetailsByName = (siteName: string) => {
  return apiInstance.get<SiteDetails>(`/Site/GetSiteByName/${siteName}`);
  // If your API uses an ID: return apiInstance.get<SiteDetails>(`/Site/GetSiteById/${siteId}`);
};

// --- NEW FUNCTION TO UPDATE SITE DETAILS ---
// Using POST request to /Site/SaveSite with complete site data
export const updateSite = (siteId: string, siteData: any) => {
  // The payload should already contain all required fields including id, customerId, etc.
  return apiInstance.post('/Site/SaveSite', siteData);
};


export const downloadPlantCsv = () => {
  return apiInstance.get(`/Plant/Download`, {
    responseType: 'blob',
  });
};

export const downloadSiteCsv = () => {
  return apiInstance.get(`/Site/Download`, {
    responseType: 'blob',
  });
};
// Services/serviceURL.ts

export interface PinPayload {
  assetId: string;     // the asset's ID
  isPinned: boolean;   // true → pin, false → unpin
}

export interface PinResponse {
  Success: boolean;
  Message: string;
  Data: any;  // Response data from backend
  StatusCode: number;
}

// POST /Asset/Pin
export const pinAssetApi = (payload: PinPayload) =>
  apiInstance.post<PinResponse>("/Asset/Pin", payload);

export const downloadCustomerCsv = () => {
  return apiInstance.get(`/Customer/Download`, {
    responseType: 'blob',
  });
};
export interface CreateCustomerPayload {
  id: string;
  createdBy: string;
  modifiedBy: string;
  createdOn: string;
  modifiedOn: string;
  isDeleted: boolean;
  customerId: string;
  customerName: string;
  soldTo: string;
  CnxCustomerId: string; // Changed from cnxCustomerId to CnxCustomerId (capital C)
  country: string;
  salesDistrict: string;
  salesRegion: string;
  state: string;
  city: string;
}


export const getSitesLiteByCustomerId = async (customerId: string) => {
  try {
    const response = await apiInstance.get(`/Site/GetSitesLite/${customerId}`);
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching sites lite:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

export interface Parameter {
  sensorName: string;
  displayName: string;
}

export interface SubAsset {
  id: string;
  type: string;
  name: string;
  status: string;
  parameters: Parameter[];
}

export interface Asset {
  id: string;
  type: string;
  name: string;
  status: string;
  isDeleted: boolean;
  parameters: Parameter[];
  subAssets: SubAsset[];
}

export interface CreatePlantPayload {
  id?: string; // Optional for create, API generates this
  customerName: string;
  siteName: string;
  plantName: string;
  plantStatus: string;
  cnxSiteId: number;
  latitude: string;
  longitude: string;
}

export interface CreatePlantEditPayload {
  id: string;
  customerName: string;
  siteName: string;
  plantName: string; // Correct field name as per Swagger documentation
  plantStatus: string;
  cnxSiteId: number;
  latitude: string;
  longitude: string;
}


export const createPlant = async (plantData: CreatePlantPayload) => {
  try {
    const response = await apiInstance.post('/Plant/SavePlant', plantData);
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error creating plant:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers,
      },
    });

    // If the API returns an error response with data, preserve it
    if (axiosError.response?.data) {
      // Create a new error that preserves the API response
      const apiError = new Error(axiosError.message);
      (apiError as any).response = axiosError.response;
      throw apiError;
    }

    throw error;
  }
};


export const editPlant = async (plantData: CreatePlantEditPayload) => {
  try {
    // Ensure the ID is present and not empty for edit operations
    if (!plantData.id || plantData.id.trim() === '') {
      throw new Error('Plant ID is required for edit operations');
    }
    
    const response = await apiInstance.post('/Plant/SavePlant', plantData);
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error editing plant:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers,
      },
    });

    // If the API returns an error response with data, preserve it
    if (axiosError.response?.data) {
      // Create a new error that preserves the API response
      const apiError = new Error(axiosError.message);
      (apiError as any).response = axiosError.response;
      throw apiError;
    }

    throw error;
  }
};

interface AssetTypeParameter {
  tag: string;
  sensorName: string;
  channelId?: string;
  position?: string;
  units?: {
    code: string;
    description: string;
  }[];
  isUserConfigurable?: boolean;
}

export interface AssetTypeResponse {
  typeName: string;
  allowSubAssets: boolean;
  isUserConfigurable: boolean;
  controllerType?: string; // Optional as it may not be present in all responses
  parameters: AssetTypeParameter[];
}

export const getAssetTypes = async () => {
  try {
    const response = await apiInstance.get('/AssetType/Get');
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching asset types:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

// Interface for UOM (Unit of Measurement) validation
export interface UomOption {
  code: string;
  description: string;
}

export interface SensorUomResponse {
  id: string;
  sensorName: string;
  uoms: UomOption[];
}

// Function to get UOMs for validation
export const getUomsForValidation = async (): Promise<SensorUomResponse[]> => {
  try {
    const response = await apiInstance.get('/AssetType/GetUoms');

    // Check if response has the expected structure with Success and Data
    if (response.data && response.data.Success && Array.isArray(response.data.Data)) {
      return response.data.Data;
    } else if (Array.isArray(response.data)) {
      // Fallback: if response.data is directly an array
      return response.data;
    } else {
      console.warn('Unexpected UOMs response structure:', response.data);
      return [];
    }
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching UOMs:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

// Interface for Plant Asset Parameter
export interface PlantAssetParameter {
  tag: string;
  sensorName: string;
  displayName: string;
  unit: string;
  unitDescription: string;
}

// Interface for Plant Asset Creation (Flat Structure)
export interface CreatePlantAssetPayload {
  PlantId: string;
  AssetId?: string;  // Optional for create, required for update
  Type: string;
  Name: string;
  Status: string;
  Parameters: PlantAssetParameter[];
  ControllerType: string;
  ControllerDeviceId: string;
  GatewayDeviceType: string;
  GatewayDeviceId: string;
  SimNumber: string;
}

// Function to create a plant asset
export const createPlantAsset = async (assetData: CreatePlantAssetPayload) => {
  try {
    const response = await apiInstance.post('/Asset/SaveAsset', assetData);
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error creating plant asset:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

// Interface for Asset Details Response
export interface AssetAttribute {
  sensorName:string;
  name: string;
  channelId: string;
  position: string;
  unit: string;
  unitDescription: string;
  value: number; // Changed from string to number to match API response
  thresholdL?: number | null;
  thresholdH?: number | null;
  setpoint?: number | null;
}

export interface AssetAlert {
  alertTime: string;
  reason: string;
  attribute: string;
  value: number;
  severity: string;
}

export interface AssetDetailsResponse {
  plant: string;
  assetType: string;
  assetName: string;
  status: string;
  controllerDeviceId: string;
  gatewayDeviceId: string;
  lastCommunication: string;
  firstCommunication:string;
  attributes: AssetAttribute[];
  alerts: AssetAlert[]; // Changed from relays to alerts
  ioTDeviceConnectionState: string;
}

// Function to get asset details by ID
export const getAssetDetails = async (assetId: string): Promise<AssetDetailsResponse> => {
  try {
    const response = await apiInstance.get(`/Asset/GetDetails/${assetId}`);
    // Return the Data field if it exists, otherwise return the response data directly
    return response.data.Data || response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching asset details:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

// Function to get asset alerts by ID and date range
// Function to get asset alerts by ID and date range WITH TIMEZONE
export const getAssetAlerts = async (
  assetId: string, 
  startDate: string, 
  endDate: string,
  timeRange:number
): Promise<AssetAlert[]> => {
  try {
    const requestPayload = {
      assetId: assetId,
      startDate: startDate,
      endDate: endDate,
      timeRange,
    };
    

    const response = await apiInstance.post('/Asset/GetAlerts', requestPayload);
    
    // Return the Data field if it exists, otherwise return the response data directly
    return response.data.Data || response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching asset alerts:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

// Interface for Asset Summary Item - based on API response
export interface AssetSummaryItem {
  id: string;
  assetId: string;
  isPinned: boolean;
  customerId: string;
  customerName: string;
  siteId: string;
  siteName: string;
  plantId: string;
  plantName: string;
  assetType: string;
  assetName: string;
  status: string;
  alerts: AlertItem[];
  alertsCount: string;
  lastCommunication: string;
  firstCommunication:string;
  hardwareHealth: string | null;
  assetHealth: string | null;
  gatewayDeviceId: string;
  simNumber: string;
  latitude?: string; // Added latitude field
  longitude?: string; // Added longitude field
}

// Interface for Alert Item
export interface AlertItem {
  thresholdL: number;
  thresholdLL: number;
  thresholdH: number;
  thresholdHH: number;
  alertTime: string;
  reason: string | null;
  attribute: string;
  value: number;
  severity: string;
}

// Add this function to fetch sales districts
export async function getSalesDistrict() {
  // This matches the API call in CreateCustomerModal.tsx
  // You may want to adjust the return type as needed
  return await apiInstance.get('/OnBoarding/GetSalesDistrict');
}

// API Response wrapper interface
export interface ApiResponse<T> {
  Success: boolean;
  Message: string;
  Data: T;
  StatusCode: number;
  StackTrace: string | null;
}

// Function to fetch units of measurements
export const getUnitsOfMeasurements = async (): Promise<string[]> => {
  try {
    const response = await apiInstance.get<ApiResponse<string[]>>('/OnBoarding/GetUnitsOfMeasurements');

    if (response.data.Success && response.data.Data) {
      return response.data.Data;
    } else {
      throw new Error(response.data.Message || 'Failed to fetch units of measurements');
    }
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching units of measurements:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

// Interface for Timezone Response
export interface TimezoneOption {
  id: string;
  text: string;
  value: string;
}

// Function to fetch timezones
export const getTimezones = async (): Promise<TimezoneOption[]> => {
  try {
    const response = await apiInstance.get<ApiResponse<TimezoneOption[]>>('/Timezone/GetTimezones');

    if (response.data.Success && response.data.Data && Array.isArray(response.data.Data)) {
      return response.data.Data;
    } else {
      throw new Error(response.data.Message || 'Failed to fetch timezones');
    }
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching timezones:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

// Interface for User Preferences Response
export interface UserPreferencesResponse {
  timeZone: string;
  uom: string;
}

// Function to fetch user preferences
export const getUserPreferences = async (userId: string): Promise<UserPreferencesResponse | null> => {
  try {
    const response = await apiInstance.get<ApiResponse<UserPreferencesResponse> | UserPreferencesResponse>(`/User/GetPreferences`);

    // Check if response is wrapped in ApiResponse format
    if (response.data && 'Success' in response.data) {
      const apiResponse = response.data as ApiResponse<UserPreferencesResponse>;
      if (apiResponse.Success && apiResponse.Data) {
        return apiResponse.Data;
      } else {
        // API returned success=false or no data - user might not have preferences yet
        return null;
      }
    }
    
    // Check if response is direct object format: { timeZone: "string", uom: "string" }
    if (response.data && typeof (response.data as UserPreferencesResponse).timeZone === 'string' && typeof (response.data as UserPreferencesResponse).uom === 'string') {
      return response.data as UserPreferencesResponse;
    }
    
    // If response.data exists but doesn't match expected format, check for Data property
    if (response.data && (response.data as any).Data) {
      const data = (response.data as any).Data;
      if (data && typeof data.timeZone === 'string' && typeof data.uom === 'string') {
        return data;
      }
    }
    
    // No valid data found
    return null;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    
    // If it's a 404 or the user doesn't have preferences, return null instead of throwing
    if (axiosError.response?.status === 404) {
      return null;
    }
    
    console.error('Error fetching user preferences:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    
    // For other errors, return null to allow the component to handle gracefully
    return null;
  }
};
// Interface for Save User Preferences Payload
export interface SaveUserPreferencesPayload {
  timeZone: string;
  uom: string;
}

// Function to save user preferences
export const saveUserPreferences = async (userId: string, preferences: { timeZone: string; uom: string }): Promise<UserPreferencesResponse> => {
  try {
    const payload: SaveUserPreferencesPayload = {
      timeZone: preferences.timeZone,
      uom: preferences.uom
    };

    const response = await apiInstance.post<ApiResponse<UserPreferencesResponse | null>>('/User/SavePreferences', payload);

    // API returns wrapped format: { Success: boolean, Message: string, Data: null, StatusCode: number, StackTrace: string | null }
    if (response.data && response.data.Success) {
      // Since API returns Data as null on success, return the original preferences
      return {
        timeZone: preferences.timeZone,
        uom: preferences.uom
      };
    } else {
      const errorMessage = response.data?.Message || 'Failed to save user preferences';
      throw new Error(errorMessage);
    }
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error saving user preferences:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

// Telemetry query interfaces and function
export interface TelemetryQueryRequest {
  AssetId: string;  // Changed to PascalCase
  ChannelIds: string[];  // Changed to PascalCase
  StartTime?: string;  // Changed to PascalCase
  EndTime?: string;  // Changed to PascalCase
  timeRange?: number;
}

// Interface for Telemetry Download Request
export interface TelemetryDownloadRequest {
  AssetId: string;
  ChannelIds: string[];
  StartTime?: string;
  EndTime?: string;
  timeRange?: number;
}

export interface TelemetryQueryPayload {
  req: TelemetryQueryRequest;
}

export interface TelemetryDataPoint {
  timestamp: string;
  value: number | null;
}

export interface TelemetryChannelData {
  assetId: string;
  sensorName: string;
  channelId: string;
  uom: string; // Unit of measurement
  data: TelemetryDataPoint[];
  minValue: number;
  maxValue: number;
  // Add alarm/threshold data
  alarmHData?: { timestamp: string; value: number }[];
  alarmHHData?: { timestamp: string; value: number }[];
  alarmLData?: { timestamp: string; value: number }[];
  alarmLLData?: { timestamp: string; value: number }[];
}

export interface UomRange {
  uom: string;
  minValue: number;
  maxValue: number;
}

export interface TelemetryResponseData {
  telemetryData: TelemetryChannelData[];
  uomRanges: UomRange[];
  noOfYAxes: number;
}

export interface TelemetryQueryResponse {
  Success: boolean;
  Message: string;
  Data: TelemetryResponseData;
  StatusCode: number;
  StackTrace: string | null;
}

export const queryTelemetryData = async (request: TelemetryQueryRequest) => {
  try {
    // Try sending the request directly without wrapping in 'req' field
    const response = await apiInstance.post<TelemetryQueryResponse>('/Telemetry/query', request);
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error querying telemetry data:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

// Function to download telemetry data
export const downloadTelemetryData = async (request: TelemetryDownloadRequest) => {
  try {
    const response = await apiInstance.post('/Telemetry/Download', request, {
      responseType: 'blob',
    });
    return response;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error downloading telemetry data:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

// Status Query Interface - Alternative structure
export interface StatusQueryRequest {
  assetId: string;
  channelIds: string[];
  startTime: string;
  endTime: string;
  timeRange: number;
}

export interface StatusQueryResponse {
  timestamp: string;
  value: number;
  channelId: string;
}

// Status Query API - Use POST method only
export const postStatusQuery = (request: StatusQueryRequest) => {
  // Get user's timezone if not provided in request
  const payload = {
    ...request,
    
  };
  
  
  return apiInstance.post<StatusQueryResponse[]>('/Status/query', payload);
};

// Alerts API interfaces and functions
export interface AlertParameter {
  tag: string;
  sensorName: string;
  displayName: string;
  unit: string;
}

// Request payload interface for GetAll alerts API
export interface AlertRequestPayload {
  startDate?: string;
  endDate?: string;
  alarmStatus: string;
  timeRange?: number;
}

export interface AlertResponse {
  id: string;
  customerId: string;
  siteId: string;
  plantId: string;
  assetId: string;
  assetType: string;
  assetName: string;
  alertType: string; // Changed from number to string
  attribute: string;
  actualMeasurement: number;
  thresholdLL: number;
  thresholdL: number;
  thresholdH: number;
  thresholdHH: number;
  startDate: string;
  endDate: string | null;
  status: string;
  customerName: string;
  siteName: string;
  plantName: string;
  isAcknowledged: boolean;
  canAcknowledge: boolean;
  parameters?: AlertParameter[]; // Made optional for backward compatibility
}

export interface AlertApiResponse {
  Success: boolean;
  Message: string;
  Data: AlertResponse[];
  StatusCode: number;
  StackTrace: string | null;
}

// Function to get all alerts
// Updated getAllAlerts function with new POST payload structure
export const getAllAlerts = async (param: any, timeZone: string): Promise<AlertResponse[]> => {
  try {
    
    // Create the request payload based on the new API structure
    const payload: AlertRequestPayload = {
      alarmStatus: param.alarmStatus || 'All'
    };

    // Add timeRange based on the param structure
    if (param.timeRange !== undefined) {
      payload.timeRange = param.timeRange;
    } else {
      // Default to custom range if start/end times are provided
      payload.timeRange = 6;
    }

    // Only include dates for custom range (timeRange = 6)
    if (payload.timeRange === 6) {
      if (param.startTime || param.startDate) {
        payload.startDate = param.startTime || param.startDate;
      }
      if (param.endTime || param.endDate) {
        payload.endDate = param.endTime || param.endDate;
      }
    }
    
    
    const response = await apiInstance.post<AlertResponse[]>(`/Alert/GetAll`, payload);
    
    // Handle both wrapped and direct response formats
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data && (response.data as any).Success && (response.data as any).Data) {
      return (response.data as any).Data;
    }
    
    return [];
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching all alerts:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers,
        data: axiosError.config?.data
      }
    });
    throw error;
  }
};
// Notification Logs API interfaces and functions
export interface NotificationLogResponse {
  id: string;
  notificationSentAt: string;
  assetId: string;
  escalationLevel: number;
  alarmCount: number;
  deliveryStatus: number;
  deliveryFormat: number;
  recipients: string[];
  content: string;
}

export interface NotificationLogsApiResponse {
  Success: boolean;
  Message: string;
  Data: NotificationLogResponse[];
  StatusCode: number;
  StackTrace: string | null;
}

// Function to get notification logs
export const getNotificationLogs = async (): Promise<NotificationLogResponse[]> => {
  try {
    const response = await apiInstance.get<NotificationLogsApiResponse>('/Alert/GetNotificationLogs');

    if (response.data.Success) {
      return response.data.Data;
    } else {
      return [];
    }
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error fetching notification logs:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    return [];
  }
};

// Function to acknowledge alerts by IDs
export const acknowledgeAllAlerts = async (alertIds: string[]): Promise<string[]> => {
  try {
    // Send raw array of strings, not wrapped in an object
    const response = await apiInstance.post('/Alert/Acknowledge', alertIds);
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error acknowledging alerts:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

export interface AssignedUsers {
  assetId: string;
  isAcknowledgementRequired?: boolean;
  users: string[];
}


// function to get notification summary details
export const getNotificationSummary = async (PlantID: string | null) => {
  try {
    const response = await apiInstance.get(`/AssetUserAssignment/GetSummary/${PlantID}`);
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error Notification Summary types:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

// function to get User by customer ID
export const getUserByCustomerID = async (CustomerID: string | null) => {
  try {
    const response = await apiInstance.get(`/User/GetUsersLite/${CustomerID}`);
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error Notification Summary types:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    throw error;
  }
};

//function to post the assigned Users
export const assignUsers = async (assignedUsers: AssignedUsers) => {
  try {
    const payload = [assignedUsers];
    const response = await apiInstance.post('/AssetUserAssignment/AssignUsers', payload, {
      validateStatus: function (status) {
        // Accept both success and client error status codes
        return status >= 200 && status < 500;
      }
    });

    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;

    console.error('Error saving user:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });

    // Return a generic error response if we can't get the API response
    return {
      Success: false,
      Message: 'Failed to save user. Please try again.',
      Data: {},
      StatusCode: 500
    };
  }
}
// Interface for Add/Update Port Forwarding Config payload

export interface AddPortForwardingConfigPayload {
  id: string; // Required field for API - use empty string for new configs or GUID for updates
  createdBy?: string;
  modifiedBy?: string;
  createdOn?: string;
  modifiedOn?: string;
  isDeleted?: boolean;
  serviceName: string;
  inputPort: number;
  targetPort: number;
  targetIpAddress: string;
  protocol: string;
  accessLimit: number; // Access limit in seconds
  tls: boolean;
  deviceId: string;
  createdTime?: number;
  endpoint?: string;
  expired?: boolean;
  expiredTime?: number;
  hostname?: string;
  imsi?: string;
  accessFrom?: string;
  sourceIPAddress?: string;
  operatorId?: string;
  placement?: string;
  port?: number;
  simId?: string;
  duration?: number;
  errorMessage?: string;
  userName?: string;
  isPortFromOnlyDevice?: boolean;
  currentUserIpAddress?: string; // Should be sent as empty string
  destinationIpAddress?: string; 
  destinationStrategy?: string; 
}



// Function to add or update port forwarding configuration

export const addOrUpdatePortForwardingConfig = async (configData: AddPortForwardingConfigPayload) => {
  try {
    // Use the existing apiInstance with default baseURL
    const response = await apiInstance.post(
      '/PortForwardingConfig/AddOrUpdatePortForwardingConfig', 
      configData
    );

    return response.data;

  } catch (error: unknown) {

    const axiosError = error as import('axios').AxiosError;

    // If it's a validation error (400), extract the validation messages

    if (axiosError.response?.status === 400 && axiosError.response?.data) {
      const errorData = axiosError.response.data as any;
      if (errorData.errors) {
        console.error('Validation errors:', errorData.errors);
      }
    }

    throw axiosError;

  }

};

// Port forwarding configuration interface

export interface PortForwardingConfig {
  id: string;
  createdBy?: string;
  modifiedBy?: string;
  createdOn?: string;
  modifiedOn?: string;
  isDeleted?: boolean;
  serviceName: string;
  inputPort: number;
  targetPort: number;
  targetIpAddress: string;
  protocol: string;
  accessLimit: number;
  tls: boolean;
  deviceId: string;
  requestedTime?: string; // New field from latest API
  createdTime?: number;
  endpoint?: string;
  expired?: boolean;
  expiredTime?: number;
  hostname?: string;
  imsi?: string;
  accessFrom?: string;
  sourceIPAddress?: string;
  operatorId?: string;
  placement?: string;
  port?: number;
  simId?: string;
  duration?: number;
  errorMessage?: string;
  userName?: string;
  isPortFromOnlyDevice?: boolean;
  currentUserIpAddress?: string;
  destinationIpAddress?: string;
  destinationStrategy?: string;
}

// Function to get gateway device ID from asset details

export const getGatewayDeviceId = async (assetId: string): Promise<string | null> => {
  try {


    const response = await apiInstance.get(`/Asset/GetDetails/${assetId}`);
    const gatewayDeviceId = response.data?.Data?.gatewayDeviceId;

    if (gatewayDeviceId) {
      return gatewayDeviceId;
    } else {
      console.warn('Gateway Device ID not found in asset details');

      return null;
    }

  } catch (error: unknown) {

    const axiosError = error as import('axios').AxiosError;

    throw axiosError;
  }

};

// Function to get port forwarding configurations by gateway device ID

export const getPortForwardingConfigs = async (assetId: string): Promise<PortForwardingConfig[]> => {

  try {

    // First, get the gateway device ID from asset details
    const gatewayDeviceId = await getGatewayDeviceId(assetId);

    if (!gatewayDeviceId) {

      console.warn('Cannot fetch port forwarding configs: Gateway Device ID not found');
      return [];

    }


    // Use the existing apiInstance with default baseURL
    const response = await apiInstance.get(
      `/PortForwardingConfig/GetPortforwardingConfigsByDeviceId/${gatewayDeviceId}`
    );


    return response.data?.Data || [];

  } catch (error: unknown) {

    const axiosError = error as import('axios').AxiosError;

    if (axiosError.response?.status === 404) {

      console.warn('Port forwarding configs endpoint not available in dev environment');

      console.warn(' The UI will show "No configurations found"');

      return []; // Return empty array to prevent UI crash

    }

    // For other errors (auth, server errors, etc.), log and throw

    throw axiosError;

  }

};

// Function to check if device is connected by device ID
export const checkIsDeviceConnectedByDeviceId = async (deviceId: string): Promise<boolean> => {
  try {

    const response = await apiInstance.get(
      `/PortForwardingConfig/CheckIsDeviceConnectedByDeviceId/${deviceId}`
    );

    
    // The API returns data in the standard format: {Success: true, Data: true, StatusCode: 200, ...}
    // Extract the boolean value from the Data field
    if (response.data && response.data.Success) {
      return response.data.Data === true || response.data.Data === 'true';
    }
    
    // Fallback for direct boolean response
    return response.data === true || response.data === 'true';

  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;

    console.error('Error checking device connection:', {
      message: axiosError.message,
      status: axiosError.response?.status,
      statusText: axiosError.response?.statusText,
      data: axiosError.response?.data
    });

    // Return false if we can't determine connection status
    return false;
  }
};

// Function to get port forwarding configuration by ID
export const getPortForwardingConfigById = async (configId: string): Promise<PortForwardingConfig | null> => {
  try {

    const response = await apiInstance.get(
      `/PortForwardingConfig/GetPortforwardingConfigById/${configId}`
    );

    
    // The API returns data in the standard format: {Success: true, Data: {...}, StatusCode: 200, ...}
    if (response.data && response.data.Success) {
      return response.data.Data;
    }
    
    // Fallback for direct config response
    return response.data;
    
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;

    console.error('Error fetching port forwarding config by ID:', {
      message: axiosError.message,
      status: axiosError.response?.status,
      statusText: axiosError.response?.statusText,
      data: axiosError.response?.data
    });
    
    // Return null if we can't fetch the config
    return null;
  }
};
export const GetDocumentByAssetID = async (assetId: string|undefined) => {

  try {
    const response = await apiInstance.get(`AssetDocument/Get/${assetId}`);
    return response.data.Data;


  } catch (error: unknown) {

    const axiosError = error as import('axios').AxiosError;

    throw axiosError;
  }
};
export const UploadDocumentByAssetID = async (assetId: string, files: File[],documentTitle:string) => {
  try {
    const formData = new FormData();

    // append each selected file
    files.forEach((file) => {
      formData.append("files", file);
      formData.append("name", documentTitle);
    });

    const response = await apiInstance.post(
      `AssetDocument/Upload/${assetId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;

  } catch (error: unknown) {
    const axiosError = error as import("axios").AxiosError;
    console.error("Upload Error:", axiosError);
    throw axiosError;
  }
};
export const DeleteDocumentByID = async (documentIds:string[]) => {

  try {
    const response = await apiInstance.post(
      "AssetDocument/Delete",
      documentIds,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error: unknown) {
    const axiosError = error as import("axios").AxiosError;
    console.error("Delete failed:", axiosError);
    throw axiosError;
  }
};

//download
export const DownloadDocumentByID = async (documentId: string[]) => {
  try {
   
    const response = await apiInstance.get(
      `AssetDocument/DownloadDocument/${documentId}`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        responseType: "blob", // 👈 important for binary file download
      }
    );


    // Create a downloadable blob
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    // Extract filename from headers if present
    const contentDisposition = response.headers["content-disposition"];
    const fileNameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
    const fileName = fileNameMatch ? fileNameMatch[1] : "downloaded-file";

    // Trigger the download
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    link.remove();
    window.URL.revokeObjectURL(url);

    return { StatusCode: response.status, Message: "Download successful" };
  } catch (error: unknown) {
    const axiosError = error as import("axios").AxiosError;
    console.error("Download failed:", axiosError);
    throw axiosError;
  }
};

// Function to download config files for an asset
export const downloadConfigFiles = async (assetId: string) => {
  try {
    const response = await apiInstance.get(`/Asset/DownloadConfigFiles/${assetId}`, {
      responseType: 'blob'
    });

    if (response.status === 200) {
      // Check if response data exists and has size > 0
      if (response.data && response.data.size > 0) {
        // Create blob URL and trigger download
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Try to get filename from Content-Disposition header
        const contentDisposition = response.headers['content-disposition'];
        let filename = 'config_files.zip'; // default filename
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true, message: 'Config files downloaded successfully' };
      } else {
        return { success: false, message: 'No config files received from server' };
      }
    } else {
      return { success: false, message: `Server responded with status ${response.status}` };
    }
  } catch (error: unknown) {
    const axiosError = error as import('axios').AxiosError;
    console.error('Error downloading config files:', {
      message: axiosError.message,
      response: axiosError.response,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers
      }
    });
    
    // Extract error message from API response if available
    let errorMessage = 'An unexpected error occurred while downloading config files';
    
    if (axiosError.response?.data) {
      try {
        let responseData = axiosError.response.data;
        
        // If response data is a Blob (due to responseType: 'blob'), try to parse it as JSON
        if (responseData instanceof Blob) {
          const text = await responseData.text();
          try {
            responseData = JSON.parse(text);
          } catch {
            // If JSON parsing fails, use the text as the error message
            errorMessage = text || axiosError.message || errorMessage;
            return { success: false, message: errorMessage };
          }
        }
        
        // Extract the error message from the API response
        const apiResponse = responseData as any;
        if (apiResponse.Message) {
          // Clean up the message by taking the first meaningful line
          const fullMessage = apiResponse.Message;
          const firstLine = fullMessage.split('\n')[0];
          errorMessage = firstLine || fullMessage;
        } else if (apiResponse.message) {
          errorMessage = apiResponse.message;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      } catch (parseError) {
        console.error('Failed to parse error response:', parseError);
        errorMessage = axiosError.message || errorMessage;
      }
    } else if (axiosError.message) {
      errorMessage = axiosError.message;
    }
    
    return { success: false, message: errorMessage };
  }
};
